import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import VerifiedBadge from '@/components/VerifiedBadge';
import VerificationDataCard from '@/components/VerificationDataCard';
import ChangePasswordCard from '@/components/ChangePasswordCard';
import { User, Mail, Phone, MapPin, Globe, Save, Loader2, Camera, Briefcase, Calendar, Shield, Trash2, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  en: {
    intent: 'Intent', budget_min: 'Budget Min', budget_max: 'Budget Max',
    preferred_property_type: 'Property type', preferred_areas: 'Preferred areas',
    timeline: 'Timeline', experience: 'Experience', country: 'Country',
    age_range: 'Age range', languages_spoken: 'Languages',
    name: 'Name', phone: 'Phone', bio: 'Bio', photo: 'Photo',
  },
  es: {
    intent: 'Intención', budget_min: 'Presupuesto mín.', budget_max: 'Presupuesto máx.',
    preferred_property_type: 'Tipo de propiedad', preferred_areas: 'Áreas preferidas',
    timeline: 'Plazo', experience: 'Experiencia', country: 'País',
    age_range: 'Edad', languages_spoken: 'Idiomas',
    name: 'Nombre', phone: 'Teléfono', bio: 'Biografía', photo: 'Foto',
  },
};

const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria",
  "Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan",
  "Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia",
  "Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica",
  "Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic","East Timor","Ecuador",
  "Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France",
  "Gabon","Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau",
  "Guyana","Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland",
  "Israel","Italy","Ivory Coast","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kosovo",
  "Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania",
  "Luxembourg","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius",
  "Mexico","Micronesia","Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia",
  "Nauru","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway",
  "Oman","Pakistan","Palau","Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland",
  "Portugal","Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines",
  "Samoa","San Marino","Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore",
  "Slovakia","Slovenia","Solomon Islands","Somalia","South Africa","South Korea","South Sudan","Spain","Sri Lanka","Sudan",
  "Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand","Togo","Tonga",
  "Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine","United Arab Emirates",
  "United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
];

export default function ProfilePage() {
  const { user, token, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({});

  const headers = { Authorization: `Bearer ${token}` };
  const tt = (en, es) => language === 'es' ? es : en;

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/users/profile`, { headers })
      .then(r => {
        setProfile(r.data);
        setForm({
          name: r.data.name || '',
          phone: r.data.phone || '',
          bio: r.data.bio || '',
          photo: r.data.photo || '',
          country: r.data.country || '',
          state_region: r.data.state_region || '',
          city_suburb: r.data.city_suburb || '',
          age_range: r.data.age_range || '',
          intent: r.data.intent || '',
          budget_min: r.data.budget_min || '',
          budget_max: r.data.budget_max || '',
          preferred_property_type: r.data.preferred_property_type || '',
          timeline: r.data.timeline || '',
          experience: r.data.experience || '',
          languages_spoken: r.data.languages_spoken || [],
          preferred_areas: r.data.preferred_areas || [],
          instagram_handle: r.data.instagram_handle || '',
        });
      })
      .catch(() => toast.error(tt('Failed to load profile', 'Error al cargar perfil')))
      .finally(() => setLoading(false));
  }, [token]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const data = { ...form };
      if (data.budget_min) data.budget_min = parseFloat(data.budget_min);
      if (data.budget_max) data.budget_max = parseFloat(data.budget_max);
      if (!data.budget_min) delete data.budget_min;
      if (!data.budget_max) delete data.budget_max;
      await axios.put(`${API}/users/profile`, data, { headers });
      toast.success(tt('Profile saved!', 'Perfil guardado!'));
      const r = await axios.get(`${API}/users/profile`, { headers });
      setProfile(r.data);
      // Fire feedback prompt when profile reaches 100% completion (backend gates once-per-user)
      const prevPct = profile?.completion ?? 0;
      const newPct = r.data?.completion ?? 0;
      if (newPct >= 100 && prevPct < 100) {
        try { window.__wakaFeedback?.fire?.('profile_complete'); } catch {}
      }
    } catch { toast.error(tt('Failed to save', 'Error al guardar')); }
    setSaving(false);
  };

  const uploadPhoto = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setForm(f => ({ ...f, photo: res.data.url }));
      toast.success(tt('Photo uploaded!', 'Foto subida!'));
    } catch {
      setForm(f => ({ ...f, photo: `uploaded://${file.name}` }));
    }
  };

  if (loading) return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-4">
      <Skeleton className="h-10 w-48" /><Skeleton className="h-64 rounded-xl" />
    </div>
  );

  const photoSrc = (() => {
    const p = form.photo;
    if (!p || p.startsWith('uploaded://')) return '';
    if (p.startsWith('http://') || p.startsWith('https://') || p.startsWith('data:')) return p;
    if (p.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${p}`;
    return p;
  })();

  const roleLabels = {
    user: tt('Property Seeker', 'Buscador de Propiedades'),
    agent: tt('Real Estate Agent', 'Agente Inmobiliario'),
    lawyer: tt('Lawyer', 'Abogado'),
    admin: tt('Administrator', 'Administrador'),
    superadmin: tt('Super Admin', 'Super Administrador'),
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
            {photoSrc ? (
              <img src={photoSrc} alt="" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
            ) : (
              <User className="w-8 h-8 text-primary" />
            )}
          </div>
          <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#D4AF37] text-white flex items-center justify-center cursor-pointer hover:bg-[#C8A028] transition-colors">
            <Camera className="w-3.5 h-3.5" />
            <input type="file" className="hidden" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); }} />
          </label>
        </div>
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-semibold">{form.name || profile?.email}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <Badge className="text-[10px]" variant="outline">{roleLabels[profile?.role] || profile?.role}</Badge>
            {profile?.agent_tier && <Badge className="text-[10px] bg-slate-100 text-slate-600">{profile.agent_tier}</Badge>}
            {profile?.verified && <VerifiedBadge role={profile.role} size="xs" />}
          </div>
          {profile?.completion !== undefined && (
            <div className="mt-1.5">
              <div className="flex items-center gap-2">
                <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${profile.completion}%` }} />
                </div>
                <span className="text-[10px] text-slate-400">{profile.completion}% {tt('complete', 'completo')}</span>
              </div>
              {profile?.missing_fields?.length > 0 && (
                <div className="mt-2 flex items-start gap-1.5 flex-wrap" data-testid="profile-missing-fields">
                  <span className="text-[10px] text-amber-700 font-medium mr-1">{tt('Pending:', 'Pendiente:')}</span>
                  {profile.missing_fields.map(f => (
                    <Badge key={f} className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 capitalize" variant="outline">
                      {FIELD_LABELS[language === 'es' ? 'es' : 'en'][f] || f.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Basic Info */}
      <Card className="mb-4" data-testid="profile-basic-card">
        <CardContent className="p-5 space-y-4">
          <h2 className="font-heading font-semibold text-base flex items-center gap-2">
            <User className="w-4 h-4 text-primary" />{tt('Basic Information', 'Informacion Basica')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{tt('Full Name', 'Nombre Completo')}</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="mt-1" data-testid="profile-name" />
            </div>
            <div>
              <Label className="text-xs">{tt('Phone', 'Telefono')}</Label>
              <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+58 412 123 4567" className="mt-1" data-testid="profile-phone" />
            </div>
            <div>
              <Label className="text-xs">{tt('Email', 'Correo')}</Label>
              <Input value={profile?.email || ''} disabled className="mt-1 bg-slate-50" />
            </div>
            <div>
              <Label className="text-xs">{tt('Age Range', 'Rango de Edad')}</Label>
              <Select value={form.age_range} onValueChange={v => setForm(f => ({ ...f, age_range: v }))}>
                <SelectTrigger className="mt-1" data-testid="profile-age"><SelectValue placeholder={tt('Select', 'Seleccionar')} /></SelectTrigger>
                <SelectContent>
                  {['18-24', '25-34', '35-44', '45-54', '55-64', '65+'].map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">{tt('Bio / About', 'Bio / Acerca de')}</Label>
            <Textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
              rows={3} className="mt-1" placeholder={tt('Tell us about yourself...', 'Cuentanos sobre ti...')} data-testid="profile-bio" />
          </div>
          <div>
            <Label className="text-xs">{tt('Instagram handle (optional)', 'Usuario de Instagram (opcional)')}</Label>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-sm text-slate-400">@</span>
              <Input
                value={(form.instagram_handle || '').replace(/^@/, '')}
                onChange={(e) => setForm(f => ({ ...f, instagram_handle: e.target.value.replace(/^@/, '').trim() }))}
                placeholder="wakacasa.ve"
                maxLength={32}
                data-testid="profile-instagram-handle"
              />
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              {tt('Shown on your public agent profile.', 'Aparece en tu perfil público de Agente.')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Location */}
      <Card className="mb-4" data-testid="profile-location-card">
        <CardContent className="p-5 space-y-4">
          <h2 className="font-heading font-semibold text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary" />{tt('Location', 'Ubicacion')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">{tt('Country', 'Pais')}</Label>
              <Select value={form.country} onValueChange={v => setForm(f => ({ ...f, country: v }))}>
                <SelectTrigger className="mt-1" data-testid="profile-country"><SelectValue placeholder={tt('Select Country', 'Seleccionar Pais')} /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {COUNTRIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{tt('State / Region', 'Estado / Region')}</Label>
              <Input value={form.state_region} onChange={e => setForm(f => ({ ...f, state_region: e.target.value }))}
                className="mt-1" data-testid="profile-state" />
            </div>
            <div>
              <Label className="text-xs">{tt('City', 'Ciudad')}</Label>
              <Input value={form.city_suburb} onChange={e => setForm(f => ({ ...f, city_suburb: e.target.value }))}
                className="mt-1" data-testid="profile-city" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Property Preferences (for buyers/all) */}
      <Card className="mb-4" data-testid="profile-preferences-card">
        <CardContent className="p-5 space-y-4">
          <h2 className="font-heading font-semibold text-base flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-primary" />{tt('Property Preferences', 'Preferencias de Propiedad')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{tt('Intent', 'Intencion')}</Label>
              <Select value={form.intent} onValueChange={v => setForm(f => ({ ...f, intent: v }))}>
                <SelectTrigger className="mt-1" data-testid="profile-intent"><SelectValue placeholder={tt('Select', 'Seleccionar')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="buyer">{tt('Buyer', 'Comprador')}</SelectItem>
                  <SelectItem value="renter">{tt('Renter', 'Arrendatario')}</SelectItem>
                  <SelectItem value="seller">{tt('Seller', 'Vendedor')}</SelectItem>
                  <SelectItem value="investor">{tt('Investor', 'Inversionista')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{tt('Property Type', 'Tipo de Propiedad')}</Label>
              <Select value={form.preferred_property_type} onValueChange={v => setForm(f => ({ ...f, preferred_property_type: v }))}>
                <SelectTrigger className="mt-1" data-testid="profile-proptype"><SelectValue placeholder={tt('Select', 'Seleccionar')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="house">{tt('House', 'Casa')}</SelectItem>
                  <SelectItem value="apartment">{tt('Apartment', 'Apartamento')}</SelectItem>
                  <SelectItem value="land">{tt('Land', 'Terreno')}</SelectItem>
                  <SelectItem value="warehouse">{tt('Warehouse', 'Galpon')}</SelectItem>
                  <SelectItem value="office">{tt('Office', 'Oficina')}</SelectItem>
                  <SelectItem value="commercial_unit">{tt('Commercial Unit', 'Local')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{tt('Budget Min ($)', 'Presupuesto Min ($)')}</Label>
              <Input type="number" value={form.budget_min} onChange={e => setForm(f => ({ ...f, budget_min: e.target.value }))}
                placeholder="50000" className="mt-1" data-testid="profile-budget-min" />
            </div>
            <div>
              <Label className="text-xs">{tt('Budget Max ($)', 'Presupuesto Max ($)')}</Label>
              <Input type="number" value={form.budget_max} onChange={e => setForm(f => ({ ...f, budget_max: e.target.value }))}
                placeholder="300000" className="mt-1" data-testid="profile-budget-max" />
            </div>
            <div>
              <Label className="text-xs">{tt('Timeline', 'Plazo')}</Label>
              <Select value={form.timeline} onValueChange={v => setForm(f => ({ ...f, timeline: v }))}>
                <SelectTrigger className="mt-1" data-testid="profile-timeline"><SelectValue placeholder={tt('Select', 'Seleccionar')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">{tt('Immediate', 'Inmediato')}</SelectItem>
                  <SelectItem value="3-6 months">{tt('3-6 Months', '3-6 Meses')}</SelectItem>
                  <SelectItem value="browsing">{tt('Just Browsing', 'Solo Explorando')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{tt('Experience', 'Experiencia')}</Label>
              <Select value={form.experience} onValueChange={v => setForm(f => ({ ...f, experience: v }))}>
                <SelectTrigger className="mt-1" data-testid="profile-experience"><SelectValue placeholder={tt('Select', 'Seleccionar')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="first_time">{tt('First Time', 'Primera Vez')}</SelectItem>
                  <SelectItem value="experienced">{tt('Experienced', 'Con Experiencia')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">{tt('Preferred Areas (comma-separated)', 'Áreas Preferidas (separadas por coma)')}</Label>
              <Input
                value={Array.isArray(form.preferred_areas) ? form.preferred_areas.join(', ') : (form.preferred_areas || '')}
                onChange={e => setForm(f => ({ ...f, preferred_areas: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                placeholder={tt('e.g. Caracas Este, Valencia, Maracaibo', 'ej. Caracas Este, Valencia, Maracaibo')}
                className="mt-1"
                data-testid="profile-preferred-areas"
              />
              <p className="text-[10px] text-slate-400 mt-1">{tt('Cities, neighborhoods, or regions you want to focus on.', 'Ciudades, urbanizaciones o regiones de tu interés.')}</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">{tt('Languages Spoken (comma-separated)', 'Idiomas Hablados (separados por coma)')}</Label>
              <Input
                value={Array.isArray(form.languages_spoken) ? form.languages_spoken.join(', ') : (form.languages_spoken || '')}
                onChange={e => setForm(f => ({ ...f, languages_spoken: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                placeholder={tt('e.g. Spanish, English, Portuguese', 'ej. Español, Inglés, Portugués')}
                className="mt-1"
                data-testid="profile-languages-spoken"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Verification Data (agents & lawyers only — read-only on profile) */}
      {(profile?.role === 'agent' || profile?.role === 'lawyer') && (
        <div className="mb-4">
          <VerificationDataCard mode="self" />
        </div>
      )}

      {/* Account Security — change password (all logged-in users) */}
      <ChangePasswordCard />

      {/* Save Button */}
      <Button onClick={saveProfile} disabled={saving}
        className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-2 h-12 text-base" data-testid="save-profile-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {saving ? tt('Saving...', 'Guardando...') : tt('Save Profile', 'Guardar Perfil')}
      </Button>

      <p className="text-xs text-slate-400 text-center mt-3">
        {tt('Member since', 'Miembro desde')} {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : '-'}
      </p>

      {/* Danger zone — self-delete account (Meta App Review requirement) */}
      {profile?.role !== 'superadmin' && (
        <Card className="mt-8 border-red-200" data-testid="profile-danger-zone">
          <CardContent className="p-5">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <h3 className="font-heading text-base text-red-700">
                  {tt('Delete account', 'Eliminar cuenta')}
                </h3>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  {tt(
                    'Permanently deletes your account and all associated data (listings, messages, favourites, verification documents). This action is immediate and irreversible.',
                    'Elimina permanentemente tu cuenta y todos los datos asociados (anuncios, mensajes, favoritos, documentos de verificación). Esta acción es inmediata e irreversible.',
                  )}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                size="sm"
                className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                onClick={async () => {
                  const phrase = tt('DELETE', 'ELIMINAR');
                  const confirm1 = window.prompt(
                    tt(
                      `This will permanently delete your account.\n\nType ${phrase} to confirm:`,
                      `Esto eliminará permanentemente tu cuenta.\n\nEscribe ${phrase} para confirmar:`,
                    ),
                  );
                  if (confirm1 !== phrase) return;
                  setDeleting(true);
                  try {
                    await axios.delete(`${API}/users/me`, { headers });
                    toast.success(tt('Account deleted', 'Cuenta eliminada'));
                    logout?.();
                    navigate('/');
                  } catch (e) {
                    toast.error(e.response?.data?.detail || tt('Could not delete account', 'No se pudo eliminar la cuenta'));
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
                data-testid="delete-account-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {tt('Delete my account', 'Eliminar mi cuenta')}
              </Button>
              <a
                href="/data-deletion"
                className="text-xs text-slate-500 hover:text-[#0B192C] self-center"
                data-testid="data-deletion-info-link"
              >
                {tt('Learn how data deletion works →', 'Cómo funciona la eliminación de datos →')}
              </a>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
